/**
 * The catalogue drawer — the bench's sourcing surface, pulled out over the sheet.
 *
 * Sourcing and composing are different moments and want different room: comprehensive filters, a
 * grid of real photographs and a full specimen sheet cannot share a 20rem rail with the plate and
 * the recipe. Giving the catalogue its own surface is what lets the bench underneath breathe — and
 * is why the plate is no longer boxed into a third of the page.
 *
 * One component serves both specimen kinds. A ring and a stone differ only in which filters apply
 * and which sheet renders, so they share the rail, the grid, the paging and the keyboard.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { listGemstones, listRings, type Page } from "../api/products";
import type { AuthService } from "../auth/auth-service";
import { FilterRail } from "./FilterRail";
import { buildQuery, siftRings, siftStones, type SiftFilters, type SpecimenKind, type WireFilters } from "./filters";
import { colourBranch, type GemstoneSummary, type RingDetail } from "./mapping";
import { metalDisplay } from "./enums";
import { RingSheet, StoneSheet } from "./SpecimenSheet";

const cx = (...c: Array<string | false | null | undefined>): string => c.filter(Boolean).join(" ");

const PAGE_SIZE = 24;
/** products-api enforces no `limit` cap, so the bench sets its own rather than letting a wide query
 *  pull thousands of rows through the browser. Surfaced in the UI — never a silent truncation. */
const MAX_ROWS = 240;

/** Debounce the filter object so tapping four checkboxes is one request, not four. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Make `aria-modal` true in fact, not just in markup: hold Tab inside the drawer and stop the bench
 * behind it scrolling. Without this, tabbing past the last control lands on a focused element that
 * is sitting behind the scrim, invisible.
 */
function useModal(ref: React.RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Tab" || !ref.current) return;
      const focusable = ref.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [ref]);
}

/** Some catalogue photos 404 — Nivoda offers in particular reference images that have gone. Fall
 *  back to the empty plate rather than a broken-image glyph. */
function Shot({ url }: { url: string | null }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  return <span className="cat-shot">{url && !broken ? <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} /> : <span className="no-image">no photo</span>}</span>;
}

function RingCard({ ring, selected, onSelect, onUse }: { ring: RingDetail; selected: boolean; onSelect: () => void; onUse: () => void }) {
  const photo = ring.images.find((i) => i.isDefault)?.url ?? ring.images[0]?.url ?? null;
  const stones = ring.centreStones.map((s) => s.type).filter(Boolean);
  return (
    <li>
      <button type="button" className={cx("cat-card", selected && "selected")} onClick={onSelect} onDoubleClick={onUse} aria-current={selected}>
        <Shot url={photo} />
        <span className="cat-meta">
          <span className="sku">{ring.sku}</span>
          <span className="nm">{ring.name}</span>
          <span className="cat-bits">
            {ring.metals.slice(0, 4).map((m) => (
              <span key={m.metal} className={cx("bead", metalDisplay(m.metal).swatchClass)} title={m.metal} />
            ))}
            {stones.length > 0 && <span className="cat-stones">{stones.join(" + ")}</span>}
            {ring.images.length > 1 && <span className="cat-photos">{ring.images.length} photos</span>}
          </span>
        </span>
      </button>
    </li>
  );
}

function StoneCard({ gem, selected, onSelect, onUse }: { gem: GemstoneSummary; selected: boolean; onSelect: () => void; onUse: () => void }) {
  const colour = colourBranch(gem.stone);
  const bits = [gem.stone.shape, gem.stone.clarity !== "UNSPECIFIED" ? gem.stone.clarity : null, colour?.value].filter(Boolean);
  return (
    <li>
      <button type="button" className={cx("cat-card", selected && "selected")} onClick={onSelect} onDoubleClick={onUse} aria-current={selected}>
        <Shot url={gem.thumbnailUrl} />
        <span className="cat-meta">
          <span className="sku">
            {typeof gem.stone.carat === "number" ? `${gem.stone.carat}ct` : "—"} {gem.stone.type}
          </span>
          <span className="nm">{bits.join(" · ")}</span>
          <span className="cat-bits">
            {gem.stone.source === "NIVODA" && <span className="cat-src">Nivoda</span>}
            {gem.stone.labGrown === true && <span className="cat-src">Lab-grown</span>}
            {typeof gem.price === "number" && <span className="cat-price">£{gem.price.toLocaleString("en-GB")}</span>}
          </span>
        </span>
      </button>
    </li>
  );
}

export function Catalogue({
  kind,
  service,
  wire,
  onWire,
  sift,
  onSift,
  onClose,
  onUseRing,
  onUseStone,
}: {
  kind: SpecimenKind;
  service: AuthService;
  wire: WireFilters;
  onWire: (w: WireFilters) => void;
  sift: SiftFilters;
  onSift: (s: SiftFilters) => void;
  onClose: () => void;
  onUseRing: (r: RingDetail) => void;
  onUseStone: (g: GemstoneSummary) => void;
}) {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const debounced = useDebounced(wire, 250);
  const page = { page: 1, limit };
  useModal(dialogRef);

  useEffect(() => {
    searchRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // A new query is a new result set — the old selection no longer means anything, and paging starts
  // over. Keyed on the query at a FIXED page size on purpose: `limit` is what "Load more" changes,
  // so keying on the live `page` here would reset the selection every time a tester loaded more.
  useEffect(() => {
    setSelectedId(null);
    setPhoto(null);
    setLimit(PAGE_SIZE);
  }, [kind, buildQuery(kind, debounced, { page: 1, limit: PAGE_SIZE })]);

  // One query serves both kinds; the row union is narrowed by `kind` immediately below.
  const q = useQuery<Page<RingDetail | GemstoneSummary>>({
    queryKey: ["catalogue", kind, buildQuery(kind, debounced, page)],
    queryFn: () => (kind === "ring" ? listRings(service, debounced, page) : listGemstones(service, debounced, page)),
    placeholderData: keepPreviousData,
  });

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const rings = kind === "ring" ? siftRings(rows as RingDetail[], sift) : [];
  const stones = kind === "stone" ? siftStones(rows as GemstoneSummary[], sift) : [];
  const shownCount = kind === "ring" ? rings.length : stones.length;

  const selectedRing = rings.find((r) => r.sku === selectedId) ?? null;
  const selectedStone = stones.find((g) => g.id === selectedId) ?? null;

  const pick = (id: string, firstPhoto: string | null): void => {
    setSelectedId(id);
    setPhoto(firstPhoto);
  };

  // Select the first result outright. The sheet is the point of the drawer, and an empty one is
  // both a dead third of the screen and a worse answer to "what does this ring look like".
  const firstRing = rings[0];
  const firstId = kind === "ring" ? (firstRing?.sku ?? null) : (stones[0]?.id ?? null);
  const firstPhoto =
    kind === "ring" ? (firstRing ? (firstRing.images.find((i) => i.isDefault)?.url ?? firstRing.images[0]?.url ?? null) : null) : (stones[0]?.thumbnailUrl ?? null);
  useEffect(() => {
    if (selectedId === null && firstId !== null) pick(firstId, firstPhoto);
  }, [selectedId, firstId, firstPhoto]);

  const capped = limit >= MAX_ROWS && rows.length < total;
  const canLoadMore = rows.length < total && limit < MAX_ROWS;

  return (
    <div className="cat-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section ref={dialogRef} className="cat" role="dialog" aria-modal="true" aria-label={kind === "ring" ? "Ring catalogue" : "Stone catalogue"}>
        <header className="cat-head">
          <p className="lab-eyebrow">Catalogue</p>
          <h2 className="cat-title">{kind === "ring" ? "Rings" : "Loose stones"}</h2>
          <form className="cat-search" onSubmit={(e) => e.preventDefault()}>
            <input
              ref={searchRef}
              className="input"
              placeholder={kind === "ring" ? "Search SKU or name…" : "Search stones…"}
              value={wire.search}
              onChange={(e) => onWire({ ...wire, search: e.target.value })}
            />
          </form>
          <button className="btn ghost small-btn cat-close" type="button" onClick={onClose}>
            Close <span className="mono">esc</span>
          </button>
        </header>

        <div className="cat-body">
          <FilterRail kind={kind} wire={wire} onWire={onWire} sift={sift} onSift={onSift} total={total} loaded={rows.length} shown={shownCount} page={page} />

          <div className="cat-results">
            <div className="cat-resulthead">
              <span className="mono small muted">
                {q.isFetching ? "searching…" : `${shownCount.toLocaleString()} shown`}
                {shownCount !== rows.length && ` · ${rows.length.toLocaleString()} loaded`}
                {rows.length < total && ` · ${total.toLocaleString()} match`}
              </span>
            </div>

            {q.isError && <p className="error small">products-api error: {(q.error as Error).message}</p>}
            {!q.isError && rows.length === 0 && !q.isFetching && <p className="lab-empty">Nothing in the catalogue matches this query.</p>}
            {!q.isError && rows.length > 0 && shownCount === 0 && <p className="lab-empty">All {rows.length} loaded rows were sifted out. Relax a sift filter to see them.</p>}

            <ul className="cat-grid">
              {rings.map((r) => (
                <RingCard
                  key={r.sku}
                  ring={r}
                  selected={r.sku === selectedId}
                  onSelect={() => pick(r.sku, r.images.find((i) => i.isDefault)?.url ?? r.images[0]?.url ?? null)}
                  onUse={() => onUseRing(r)}
                />
              ))}
              {stones.map((g) => (
                <StoneCard key={g.id} gem={g} selected={g.id === selectedId} onSelect={() => pick(g.id, g.thumbnailUrl)} onUse={() => onUseStone(g)} />
              ))}
            </ul>

            {canLoadMore && (
              <button className="btn lab-loadmore" type="button" onClick={() => setLimit((l) => Math.min(l + PAGE_SIZE, MAX_ROWS))} disabled={q.isFetching}>
                Load {Math.min(PAGE_SIZE, total - rows.length)} more
              </button>
            )}
            {capped && <p className="lab-empty cat-capped">Showing the first {MAX_ROWS} of {total.toLocaleString()}. Narrow the query to reach the rest.</p>}
          </div>

          {/* The sheet is a document and scrolls like one, but its action is pinned: a ring's sheet
              runs long, and "Use this ring" must never be a scroll away. */}
          <div className="cat-sheet">
            <div className="cat-sheetscroll">
              {selectedRing && <RingSheet ring={selectedRing} photo={photo} onPhoto={setPhoto} />}
              {selectedStone && <StoneSheet gem={selectedStone} photo={photo} onPhoto={setPhoto} />}
              {!selectedRing && !selectedStone && (
                <div className="cat-nosheet">
                  <span className="t">Nothing to show</span>
                  <span className="d">Pick a specimen to read everything the catalogue records about it.</span>
                </div>
              )}
            </div>
            {selectedRing && (
              <div className="cat-sheetfoot">
                <button className="btn primary sheet-use" type="button" onClick={() => onUseRing(selectedRing)} disabled={selectedRing.images.length === 0}>
                  {selectedRing.images.length === 0 ? "No photo to swap" : "Use this ring"}
                </button>
              </div>
            )}
            {selectedStone && (
              <div className="cat-sheetfoot">
                <button className="btn primary sheet-use" type="button" onClick={() => onUseStone(selectedStone)}>
                  Use this stone
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
